import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { enhanceEnvironmentsResponse } from '../services/demoDataService';
import { fetchEnvironmentsData } from '../services/environmentsApi';

/**
 * EnvironmentContext - Global state management for environment selection
 *
 * Features:
 * - Persists environment selection across page navigation
 * - Syncs with URL query parameter (?env=production)
 * - Provides useEnvironment hook for easy access
 * - Automatically updates URL when environment changes
 * - Reads URL parameter on mount/navigation
 */

const EnvironmentContext = createContext();

export const EnvironmentProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize from URL parameter if present
  const getInitialEnvironment = () => {
    const params = new URLSearchParams(location.search);
    return params.get('env') || null;
  };

  const [selectedEnvironment, setSelectedEnvironment] = useState(getInitialEnvironment);
  const [environments, setEnvironments] = useState([]);
  // False until the first fetchEnvironments() call resolves (success or
  // failure). Consumers that gate a decision on "environments is really
  // empty" vs. "hasn't loaded yet" must wait for this — `environments`
  // starts as [] and is indistinguishable from a genuinely empty tenant
  // until this flips.
  const [environmentsLoaded, setEnvironmentsLoaded] = useState(false);
  // True when the most recent fetchEnvironments() call failed. A failure
  // leaves `environments` untouched (still whatever it last successfully
  // was, [] if never successful) — this flag lets consumers tell "confirmed
  // empty" apart from "we don't actually know, the request errored."
  const [environmentsError, setEnvironmentsError] = useState(false);
  const environmentsRef = React.useRef([]);

  // Sync environment changes to URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlEnv = params.get('env');

    // If environment is set and different from URL, update URL
    if (selectedEnvironment && urlEnv !== selectedEnvironment) {
      params.set('env', selectedEnvironment);
      navigate(`${location.pathname}?${params.toString()}`, {
        replace: true,
        state: location.state,
      });
    }

    // If no environment selected but URL has one, sync from URL
    if (!selectedEnvironment && urlEnv) {
      setSelectedEnvironment(urlEnv);
    }
  }, [selectedEnvironment, location.pathname, location.search, navigate]);

  // Update environment and sync to URL
  const updateEnvironment = useCallback((env) => {
    // Don't update state - we're doing a full page reload which will read env from URL
    // setSelectedEnvironment(env); // Removed to prevent double nav render

    // Extract only range parameter to preserve
    const currentParams = new URLSearchParams(location.search);
    const range = currentParams.get('range');

    // Build new URL with only env and range
    const newParams = new URLSearchParams();
    if (env) {
      newParams.set('env', env);
    }
    if (range) {
      newParams.set('range', range);
    }

    // Always navigate to Home with full page reload (user-initiated env switch)
    try { localStorage.setItem('lumoz-last-reload-reason', JSON.stringify({ reason: 'env-switch', env, at: Date.now() })); } catch {}
    const homeUrl = `/?${newParams.toString()}`;
    window.location.href = homeUrl;
  }, [location.search]);

  // Use ref to avoid recreating fetchEnvironments when selectedEnvironment changes
  const selectedEnvironmentRef = React.useRef(selectedEnvironment);
  const updateEnvironmentRef = React.useRef(updateEnvironment);

  useEffect(() => {
    selectedEnvironmentRef.current = selectedEnvironment;
    updateEnvironmentRef.current = updateEnvironment;
  }, [selectedEnvironment, updateEnvironment]);

  // Fetch environments from API and cache in context.
  // V2 mode: sources from /v2/data/services (same inventory as Home cards).
  // Environment inventory is sourced from the v2 services endpoint.
  const fetchEnvironments = useCallback(async (stytchClient, session = null) => {
    try {
      if (!stytchClient?.session?.getTokens()?.session_jwt) {
        return;
      }

      const sessionTokens = stytchClient.session.getTokens();
      const data = await fetchEnvironmentsData(sessionTokens.session_jwt);
      setEnvironmentsError(false);

      // Inject demo environment for Lumoz tenant
      const tenantId = session?.organization_id || null;
      const enhancedData = enhanceEnvironmentsResponse(data, tenantId);
      const envList = enhancedData.environments || [];

      // Only update state if the data has actually changed (deep comparison of JSON strings)
      const newEnvString = JSON.stringify(envList);
      const prevEnvString = JSON.stringify(environmentsRef.current);

      if (newEnvString !== prevEnvString) {
        environmentsRef.current = envList;
        setEnvironments(envList);
      }

      // Set default environment if none selected — SOFT update only. The
      // URL-sync effect writes it into the URL with replace:true. This must
      // never hard-navigate: fetchEnvironments re-runs on every Stytch session
      // refresh, and a transiently-null selection would cause a full page
      // reload to Home ("console randomly refreshes" bug).
      if (!selectedEnvironmentRef.current && envList.length > 0) {
        const defaultEnv = envList.find(e => e.name.toLowerCase() === 'production')?.name || envList[0]?.name;
        if (defaultEnv) {
          setSelectedEnvironment(defaultEnv);
        }
      }
    } catch (err) {
      console.error('Failed to fetch environments:', err);
      setEnvironmentsError(true);
    } finally {
      setEnvironmentsLoaded(true);
    }
  }, []); // Empty deps - uses refs to access latest values

  const value = useMemo(() => ({
    selectedEnvironment,
    setEnvironment: updateEnvironment,
    environments,
    environmentsLoaded,
    environmentsError,
    setEnvironments,
    fetchEnvironments
  }), [selectedEnvironment, updateEnvironment, environments, environmentsLoaded, environmentsError, fetchEnvironments]);

  return (
    <EnvironmentContext.Provider value={value}>
      {children}
    </EnvironmentContext.Provider>
  );
};

// Custom hook for easy access to environment context
export const useEnvironment = () => {
  const context = useContext(EnvironmentContext);
  if (!context) {
    throw new Error('useEnvironment must be used within EnvironmentProvider');
  }
  return context;
};
